import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  hydrateAccountLinkContext,
  writeStoredSession,
} from "../utils/accountLinks";
import { clearImpersonationStorage } from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";
import { canUseWebLogin, normalizeRole } from "../utils/rbac";
import {
  FaChevronDown,
  FaEye,
  FaEyeSlash,
  FaGift,
  FaLock,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegEnvelope,
  FaStore,
  FaUser,
} from "react-icons/fa";

const PRIMARY_COLOR = "#015b9a";
const INDIA_COUNTRY_CODE = "IN";
const INDIA_REGION_DEFAULTS = { country_code: INDIA_COUNTRY_CODE };
const UTM_STORAGE_KEY = "webSignupAttribution";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const t = {
  enterValidRestaurantName: "Enter a valid restaurant name",
  enterValidPhoneEmailName: "Enter valid phone, email, and name",
  enterFullAddress: "Enter Restaurant Full Address",
  passwordMustBeAtLeast6: "Password must be at least 6 characters",
  passwordsDoNotMatch: "Passwords do not match",
  registrationSuccessful: "Registration successful",
  pleaseVerifyPhone: "Please verify your phone via OTP",
  pleaseVerifyEmail: "Please verify your email via OTP",
  enterValidPhone: "Valid 10-digit phone required",
  enterValidPhoneIntl: "Enter a valid phone number",
  enterValidEmail: "Valid email required",
  enterYourName: "Enter your name",
  enter4DigitOTP: "Enter 4-digit OTP",
  otpVerifiedSuccessfully: "OTP verified successfully",
  somethingWentWrong: "Something went wrong",
};

const normalizeCountryCode = (value, fallback = INDIA_COUNTRY_CODE) => {
  const normalized = String(value || fallback)
    .trim()
    .toUpperCase();
  return normalized || fallback;
};

const shouldSkipRegionalLookup = () => {
  if (typeof window === "undefined") return true;

  const { hostname, protocol } = window.location || {};
  return (
    protocol === "file:" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1"
  );
};

const fetchRegionalSettingsFromIp = async () => {
  if (
    typeof window === "undefined" ||
    typeof fetch !== "function" ||
    shouldSkipRegionalLookup()
  ) {
    return INDIA_REGION_DEFAULTS;
  }

  const sources = [
    async () => {
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) throw new Error("ipapi failed");
      const data = await response.json();
      return data?.country_code;
    },
    async () => {
      const response = await fetch("https://ipwho.is/");
      if (!response.ok) throw new Error("ipwho failed");
      const data = await response.json();
      if (data?.success === false) throw new Error("ipwho rejected");
      return data?.country_code;
    },
  ];

  for (const resolveCountryCode of sources) {
    try {
      const countryCode = normalizeCountryCode(await resolveCountryCode());
      if (countryCode) {
        return { country_code: countryCode };
      }
    } catch (error) {
      // Keep trying the next source and fall back to India.
    }
  }

  return INDIA_REGION_DEFAULTS;
};

const pickUtmParams = (search = "") => {
  const raw = String(search || "").trim();
  if (!raw) return {};

  const normalized = raw.startsWith("?") ? raw.slice(1) : raw;
  const params = new URLSearchParams(normalized);
  const payload = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) payload[key] = value;
  });

  return payload;
};

const pickUtmParamsFromHash = (hash = "") => {
  const raw = String(hash || "");
  const queryIndex = raw.indexOf("?");
  if (queryIndex < 0) return {};
  return pickUtmParams(raw.slice(queryIndex));
};

const readStoredUtmParams = () => {
  if (typeof window === "undefined") return {};

  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return UTM_KEYS.reduce((acc, key) => {
      if (parsed?.[key]) acc[key] = parsed[key];
      return acc;
    }, {});
  } catch (error) {
    return {};
  }
};

const persistUtmParams = (payload) => {
  if (typeof window === "undefined" || !Object.keys(payload || {}).length) return;
  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
};

const Stepper = ({ step, total = 3 }) => (
  <div style={styles.stepWrap}>
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        style={{
          ...styles.stepDot,
          ...(i <= step ? styles.stepDotActive : null),
        }}
      />
    ))}
  </div>
);

const InputRow = ({
  icon,
  placeholder,
  value,
  onChange,
  onBlur,
  type = "text",
  maxLength,
  rightSlot,
  error = false,
  inputMode,
  autoCapitalize = "none",
}) => (
  <div style={{ ...styles.inputRow, ...(error ? styles.inputRowError : null) }}>
    <div style={styles.inputIcon}>{icon}</div>
    <input
      style={styles.input}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      type={type}
      maxLength={maxLength}
      inputMode={inputMode}
      autoCapitalize={autoCapitalize}
    />
    {rightSlot ? <div style={styles.inputRight}>{rightSlot}</div> : null}
  </div>
);

const OtpInput = ({ otpDigits, otpRefs, onChange, onBackspace }) => (
  <div style={styles.otpRow}>
    {[0, 1, 2, 3].map((i) => (
      <input
        key={i}
        ref={otpRefs[i]}
        maxLength={1}
        inputMode="numeric"
        style={styles.otpInput}
        value={otpDigits[i]}
        onChange={(e) => onChange(e.target.value, i)}
        onKeyDown={(e) => onBackspace(e, i)}
      />
    ))}
  </div>
);

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurantName, setRestaurantName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationChannel, setVerificationChannel] = useState("phone");
  const [verificationTarget, setVerificationTarget] = useState("");
  const [regionalContext, setRegionalContext] = useState(INDIA_REGION_DEFAULTS);
  const [countryCodeInput, setCountryCodeInput] = useState(
    INDIA_REGION_DEFAULTS.country_code
  );
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [step1Attempted, setStep1Attempted] = useState(false);
  const [step1Touched, setStep1Touched] = useState({
    phone: false,
    email: false,
    name: false,
    fullAddress: false,
  });
  const [isCompact, setIsCompact] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  const [utmAttribution, setUtmAttribution] = useState(() =>
    readStoredUtmParams()
  );

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    const onResize = () => {
      setIsCompact(window.innerWidth < 640);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const detected = await fetchRegionalSettingsFromIp();
        if (!alive) return;

        setRegionalContext(detected || INDIA_REGION_DEFAULTS);
        setCountryCodeInput(
          normalizeCountryCode(
            detected?.country_code,
            INDIA_REGION_DEFAULTS.country_code
          )
        );
      } catch {
        if (!alive) return;
        setRegionalContext(INDIA_REGION_DEFAULTS);
        setCountryCodeInput(INDIA_REGION_DEFAULTS.country_code);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const nextPayload = {
      ...readStoredUtmParams(),
      ...pickUtmParams(typeof window !== "undefined" ? window.location.search : ""),
      ...pickUtmParams(location.search),
      ...pickUtmParamsFromHash(typeof window !== "undefined" ? window.location.hash : ""),
    };

    if (Object.keys(nextPayload).length) {
      persistUtmParams(nextPayload);
    }

    setUtmAttribution(nextPayload);
  }, [location.hash, location.search]);

  const handleOtpChange = (value, index) => {
    const digit = String(value || "")
      .replace(/\D/g, "")
      .slice(-1);

    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleBackspace = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const postJson = (url, payload) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.timeout = 15000;
      xhr.onload = () => {
        let parsed = {};
        try {
          parsed = JSON.parse(xhr.responseText || "{}");
        } catch {
          parsed = {};
        }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, data: parsed });
      };
      xhr.onerror = () => reject(new Error("Network request failed"));
      xhr.ontimeout = () => reject(new Error("Request timeout"));
      xhr.send(JSON.stringify(payload));
    });

  const countryOptions = useMemo(
    () => [
      {
        code: "IN",
        name: "India",
        flag: "🇮🇳",
        currency: "INR",
        locale: "en-IN",
        timezone: "Asia/Kolkata",
        tax: "gst",
      },
      {
        code: "AE",
        name: "United Arab Emirates",
        flag: "🇦🇪",
        currency: "AED",
        locale: "en-AE",
        timezone: "Asia/Dubai",
        tax: "vat",
      },
      {
        code: "US",
        name: "United States",
        flag: "🇺🇸",
        currency: "USD",
        locale: "en-US",
        timezone: "America/New_York",
        tax: "sales_tax",
      },
      {
        code: "GB",
        name: "United Kingdom",
        flag: "🇬🇧",
        currency: "GBP",
        locale: "en-GB",
        timezone: "Europe/London",
        tax: "vat",
      },
      {
        code: "SG",
        name: "Singapore",
        flag: "🇸🇬",
        currency: "SGD",
        locale: "en-SG",
        timezone: "Asia/Singapore",
        tax: "gst",
      },
    ],
    []
  );

  const shouldShowCountryInput =
    normalizeCountryCode(regionalContext?.country_code) !==
    INDIA_REGION_DEFAULTS.country_code;
  const effectiveCountryCode = normalizeCountryCode(
    shouldShowCountryInput ? countryCodeInput : regionalContext?.country_code,
    INDIA_REGION_DEFAULTS.country_code
  );
  const selectedCountry =
    countryOptions.find((country) => country.code === effectiveCountryCode) ||
    countryOptions[0];
  const isIndiaUser =
    selectedCountry.code === INDIA_REGION_DEFAULTS.country_code;

  const canNextFromStep0 = () => restaurantName.trim().length >= 2;

  const getStep1Validation = () => {
    const cleanPhone = String(phone || "").replace(/\D+/g, "");
    const phoneValid = isIndiaUser
      ? cleanPhone.length === 10
      : cleanPhone.length >= 6 && cleanPhone.length <= 15;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      String(email || "").trim()
    );
    const nameValid = String(name || "").trim().length >= 2;
    const addressValid = String(fullAddress || "").trim().length >= 5;

    return {
      cleanPhone,
      phoneValid,
      emailValid,
      nameValid,
      addressValid,
      countryValid: Boolean(selectedCountry?.code),
    };
  };

  const canNextFromStep1 = () => {
    const validation = getStep1Validation();
    return (
      validation.phoneValid &&
      validation.emailValid &&
      validation.nameValid &&
      validation.addressValid &&
      validation.countryValid
    );
  };

  const canSubmit = () =>
    String(password || "").length >= 6 && password === confirmPassword;

  const next = () => {
    if (step === 0 && !canNextFromStep0()) {
      toast.error(t.enterValidRestaurantName);
      return;
    }

    if (step === 1 && !canNextFromStep1()) {
      setStep1Attempted(true);
      if (String(fullAddress || "").trim().length < 5) {
        toast.error(t.enterFullAddress);
      } else {
        toast.error(t.enterValidPhoneEmailName);
      }
      return;
    }

    setStep((prev) => Math.min(prev + 1, 2));
  };

  const back = () => setStep((prev) => Math.max(prev - 1, 0));

  const signUp = async () => {
    if (!canSubmit()) {
      toast.error(
        password?.length < 6
          ? t.passwordMustBeAtLeast6
          : t.passwordsDoNotMatch
      );
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await postJson("https://qr.billsarthi.in/public/api/register", {
        restrauntName: restaurantName,
        phone,
        email,
        password,
        name,
        full_address: fullAddress?.trim(),
        referral_code: referralCode?.trim() || undefined,
        package_type: "professional",
        country_code: selectedCountry.code,
        currency_code: selectedCountry.currency,
        locale: selectedCountry.locale,
        timezone: selectedCountry.timezone,
        tax_regime: selectedCountry.tax,
        ...utmAttribution,
      });

      const data = res?.data || {};
      if (res.ok) {
        const channel =
          String(data?.verification_channel || "phone").toLowerCase() === "email"
            ? "email"
            : "phone";
        const target =
          data?.verification_target ||
          (channel === "email"
            ? String(email || "").trim()
            : String(phone || "").trim());

        setVerificationChannel(channel);
        setVerificationTarget(target);
        setOtpDigits(["", "", "", ""]);
        toast.success(
          `${t.registrationSuccessful}. ${
            channel === "email" ? t.pleaseVerifyEmail : t.pleaseVerifyPhone
          }`
        );
        setShowOtpModal(true);
      } else {
        let message = data?.message || "Registration failed";
        if (data?.errors) {
          message = Object.values(data.errors).flat().join("\n");
        }
        toast.error(message);
      }
    } catch (err) {
      const msg = String(err?.message || "");
      if (
        msg.includes("Failed to fetch") ||
        msg.includes("Network request failed") ||
        msg.includes("Request timeout")
      ) {
        toast.error(
          "Request blocked or network unreachable. Please disable blocking extensions/VPN and try again."
        );
      } else {
        toast.error(`${t.somethingWentWrong}: ${msg}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyOtp = async () => {
    const isEmailVerification = verificationChannel === "email";
    const cleanPhone = String(phone || "").trim();
    const cleanEmail = String(email || "").trim().toLowerCase();
    const cleanOtp = otpDigits.join("");

    if (isEmailVerification) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        toast.error(t.enterValidEmail);
        return;
      }
    } else if (cleanPhone.length !== 10 || !/^\d+$/.test(cleanPhone)) {
      toast.error(t.enterValidPhone);
      return;
    }

    if (cleanOtp.length !== 4 || !/^\d+$/.test(cleanOtp)) {
      toast.error(t.enter4DigitOTP);
      return;
    }

    try {
      setIsVerifying(true);
      const res = await postJson("https://qr.billsarthi.in/public/api/verifyphone", {
        ...(isEmailVerification
          ? { email: cleanEmail, otp: cleanOtp }
          : { phone: cleanPhone, otp: cleanOtp }),
        ...utmAttribution,
      });

      const data = res?.data || {};
      if (res.ok) {
        toast.success(t.otpVerifiedSuccessfully);
        const loginRes = await postJson(
          "https://qr.billsarthi.in/public/api/login/restro",
          {
            mobile_number: phone,
            password,
            ...utmAttribution,
          }
        );
        const loginData = loginRes?.data || {};

        if (loginRes.ok) {
          const user = loginData?.data || {};
          const packageType = String(user?.package_type || "").toLowerCase();
          const shouldForceWeb =
            packageType === "professional" || packageType === "pro";

          if (!canUseWebLogin(user)) {
            clearImpersonationStorage();
            resetLogRocketSession();
            localStorage.removeItem("userData");
            const roleName = normalizeRole(user);
            toast.error(
              `Web login is allowed only for Owner and Cashier. Current role: ${roleName}.`
            );
            return;
          }

          const rawIsWebAllowed =
            user?.isWebAllowed ??
            user?.isweballowed ??
            loginData?.isWebAllowed ??
            loginData?.isweballowed;

          if (
            !shouldForceWeb &&
            (rawIsWebAllowed === 0 ||
              rawIsWebAllowed === "0" ||
              rawIsWebAllowed === false)
          ) {
            clearImpersonationStorage();
            resetLogRocketSession();
            localStorage.removeItem("userData");
            toast.error("Mobile-only plan detected. Web login is not enabled.");
            return;
          }

          clearImpersonationStorage();
          const patchedLoginData = shouldForceWeb
            ? {
                ...(loginData || {}),
                data: {
                  ...(loginData?.data || {}),
                  isWebAllowed: 1,
                  isweballowed: 1,
                  package_type: user?.package_type || "professional",
                },
              }
            : loginData;

          writeStoredSession(patchedLoginData, {
            preserveAccountLinkContext: false,
          });
          await hydrateAccountLinkContext({ force: true });
          setShowOtpModal(false);
          navigate("/dashboard");
        } else {
          toast.error(loginData?.message || "Login failed. Please try logging in.");
          setShowOtpModal(false);
          navigate("/login");
        }
      } else {
        toast.error(data.message || "OTP verification failed.");
      }
    } catch (err) {
      const msg = String(err?.message || "");
      if (
        msg.includes("Failed to fetch") ||
        msg.includes("Network request failed") ||
        msg.includes("Request timeout")
      ) {
        toast.error(
          "Request blocked or network unreachable. Please disable blocking extensions/VPN and try again."
        );
      } else {
        toast.error(`${t.somethingWentWrong}: ${msg}`);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const step1Validation = getStep1Validation();
  const showPhoneError = (step1Attempted || step1Touched.phone) && !step1Validation.phoneValid;
  const showEmailError = (step1Attempted || step1Touched.email) && !step1Validation.emailValid;
  const showNameError = (step1Attempted || step1Touched.name) && !step1Validation.nameValid;
  const showAddressError =
    (step1Attempted || step1Touched.fullAddress) && !step1Validation.addressValid;

  return (
    <div
      style={{
        ...styles.page,
        padding: isCompact ? "16px" : "24px",
      }}
    >
      <div style={styles.pageInner}>
        <div style={styles.card}>
          <Stepper step={step} />

          {step === 0 && (
            <>
              <div style={styles.title}>Your restaurant</div>
              <div style={styles.subTitle}>Tell us what to call your place.</div>
              <InputRow
                icon={<FaStore />}
                placeholder="Restaurant name"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                autoCapitalize="words"
              />
            </>
          )}

          {step === 1 && (
            <>
              <div style={styles.title}>Contact details</div>
              <div style={styles.subTitle}>We&apos;ll use these to set up your account.</div>
              <InputRow
                icon={<FaPhoneAlt />}
                placeholder={isIndiaUser ? "Phone (10 digits)" : "Phone number"}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() =>
                  setStep1Touched((prev) => ({ ...prev, phone: true }))
                }
                maxLength={isIndiaUser ? 10 : 15}
                inputMode="numeric"
                error={showPhoneError}
              />
              {showPhoneError ? (
                <div style={styles.errorText}>
                  {isIndiaUser
                    ? t.enterValidPhone
                    : t.enterValidPhoneIntl}
                </div>
              ) : null}

              <InputRow
                icon={<FaRegEnvelope />}
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() =>
                  setStep1Touched((prev) => ({ ...prev, email: true }))
                }
                type="email"
                inputMode="email"
                error={showEmailError}
              />
              {showEmailError ? (
                <div style={styles.errorText}>{t.enterValidEmail}</div>
              ) : null}

              <InputRow
                icon={<FaUser />}
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() =>
                  setStep1Touched((prev) => ({ ...prev, name: true }))
                }
                autoCapitalize="words"
                error={showNameError}
              />
              {showNameError ? (
                <div style={styles.errorText}>{t.enterYourName}</div>
              ) : null}

              <InputRow
                icon={<FaMapMarkerAlt />}
                placeholder="Restaurant address"
                value={fullAddress}
                onChange={(e) => setFullAddress(e.target.value)}
                onBlur={() =>
                  setStep1Touched((prev) => ({ ...prev, fullAddress: true }))
                }
                autoCapitalize="words"
                error={showAddressError}
              />
              {showAddressError ? (
                <div style={styles.errorText}>Enter restaurant address</div>
              ) : null}

              <InputRow
                icon={<FaGift />}
                placeholder="Referral code (optional)"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                autoCapitalize="characters"
              />

              {shouldShowCountryInput ? (
                <button
                  type="button"
                  style={styles.countryBtn}
                  onClick={() => setShowCountryPicker(true)}
                >
                  <span style={styles.countryValue}>
                    {selectedCountry.flag} {selectedCountry.name}
                  </span>
                  <span style={styles.countryMeta}>
                    {selectedCountry.code} <FaChevronDown />
                  </span>
                </button>
              ) : null}
            </>
          )}

          {step === 2 && (
            <>
              <div style={styles.title}>Secure your account</div>
              <div style={styles.subTitle}>Choose a strong password.</div>
              <InputRow
                icon={<FaLock />}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.eyeBtn}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                }
              />
              <InputRow
                icon={<FaLock />}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type={showConfirmPassword ? "text" : "password"}
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    style={styles.eyeBtn}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                }
              />
            </>
          )}

          <div style={{ ...styles.btnRow, gap: isCompact ? 10 : 12 }}>
            {step > 0 ? (
              <button
                type="button"
                style={styles.ghostBtn}
                onClick={back}
                disabled={isSubmitting}
              >
                Back
              </button>
            ) : (
              <div style={styles.buttonSpacer} />
            )}

            {step < 2 ? (
              <button
                type="button"
                style={{
                  ...styles.primaryBtn,
                  opacity:
                    !(step === 0 ? canNextFromStep0() : canNextFromStep1()) ||
                    isSubmitting
                      ? 0.6
                      : 1,
                }}
                onClick={next}
                disabled={
                  !(step === 0 ? canNextFromStep0() : canNextFromStep1()) ||
                  isSubmitting
                }
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                style={{
                  ...styles.primaryBtn,
                  opacity: !canSubmit() || isSubmitting ? 0.6 : 1,
                }}
                onClick={signUp}
                disabled={!canSubmit() || isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Create account"}
              </button>
            )}
          </div>

          <div style={styles.altRow}>
            <span style={styles.altTxt}>Already have an account?</span>
            <button
              type="button"
              onClick={() => navigate("/login")}
              style={styles.altLink}
            >
              Login
            </button>
          </div>
        </div>
      </div>

      {showOtpModal ? (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>Verify OTP</div>
            <div style={styles.modalSub}>
              Code sent to{" "}
              {verificationTarget ||
                (verificationChannel === "email" ? "your email" : "your phone")}
            </div>
            <OtpInput
              otpDigits={otpDigits}
              otpRefs={otpRefs}
              onChange={handleOtpChange}
              onBackspace={handleBackspace}
            />
            <button
              type="button"
              style={{ ...styles.primaryBtn, width: "100%" }}
              onClick={verifyOtp}
              disabled={isVerifying}
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </button>
          </div>
        </div>
      ) : null}

      {showCountryPicker && shouldShowCountryInput ? (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>Select country</div>
            <div style={styles.countryList}>
              {countryOptions.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  style={{
                    ...styles.countryOption,
                    ...(option.code === effectiveCountryCode
                      ? styles.countryOptionActive
                      : null),
                  }}
                  onClick={() => {
                    setCountryCodeInput(option.code);
                    setShowCountryPicker(false);
                  }}
                >
                  <span>
                    {option.flag} {option.name}
                  </span>
                  <span style={styles.countryOptionCode}>{option.code}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              style={styles.ghostBtnFull}
              onClick={() => setShowCountryPicker(false)}
            >
              Back
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SignupPage;

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eaf3f5",
    backgroundImage:
      "linear-gradient(180deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.9)), url('/signup-bg.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  pageInner: {
    width: "100%",
    maxWidth: 420,
  },
  card: {
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.14)",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    display: "grid",
    gap: 12,
  },
  stepWrap: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    marginBottom: 4,
  },
  stepDot: {
    height: 6,
    width: 28,
    borderRadius: 999,
    backgroundColor: "#E5E7EB",
  },
  stepDotActive: {
    backgroundColor: PRIMARY_COLOR,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#111827",
  },
  subTitle: {
    marginTop: -6,
    fontSize: 13,
    color: "#6B7280",
  },
  inputRow: {
    display: "grid",
    gridTemplateColumns: "20px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 8,
    minHeight: 48,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    backgroundColor: "#F8FAFC",
  },
  inputRowError: {
    borderColor: "#F87171",
    backgroundColor: "#FEF2F2",
  },
  inputIcon: {
    color: "#6C7280",
    display: "grid",
    placeItems: "center",
  },
  input: {
    width: "100%",
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#111827",
    fontSize: 16,
    padding: "12px 0",
  },
  inputRight: {
    display: "grid",
    placeItems: "center",
  },
  eyeBtn: {
    border: "none",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    width: 40,
    height: 40,
    padding: 0,
    fontSize: 16,
  },
  errorText: {
    marginTop: -6,
    marginBottom: 4,
    color: "#B91C1C",
    fontSize: 12,
  },
  countryBtn: {
    width: "100%",
    minHeight: 48,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    backgroundColor: "#F8FAFC",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    color: "#111827",
  },
  countryValue: {
    fontSize: 14,
    fontWeight: 600,
    textAlign: "left",
  },
  countryMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#6B7280",
    fontSize: 13,
    fontWeight: 600,
  },
  btnRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 2,
  },
  buttonSpacer: {
    width: 88,
    minWidth: 88,
  },
  primaryBtn: {
    minWidth: 140,
    height: 48,
    padding: "0 18px",
    border: "none",
    borderRadius: 12,
    backgroundColor: PRIMARY_COLOR,
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  ghostBtn: {
    minWidth: 88,
    height: 44,
    padding: "0 16px",
    border: "none",
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    color: "#374151",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
  },
  ghostBtnFull: {
    width: "100%",
    height: 44,
    border: "none",
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    color: "#374151",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    marginTop: 8,
  },
  altRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
  },
  altTxt: {
    color: "#6B7280",
    fontSize: 13,
  },
  altLink: {
    border: "none",
    background: "transparent",
    color: PRIMARY_COLOR,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 50,
  },
  modal: {
    width: "min(380px, calc(100vw - 32px))",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 16,
    display: "grid",
    gap: 10,
    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.16)",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
  },
  modalSub: {
    fontSize: 13,
    color: "#6B7280",
  },
  otpRow: {
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    margin: "8px 0",
  },
  otpInput: {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: 64,
    height: 52,
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    backgroundColor: "#F8FAFC",
    color: "#111827",
    textAlign: "center",
    fontSize: 20,
    outline: "none",
  },
  countryList: {
    display: "grid",
    gap: 8,
    maxHeight: 320,
    overflowY: "auto",
    marginTop: 4,
  },
  countryOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    backgroundColor: "#F8FAFC",
    color: "#111827",
    cursor: "pointer",
    fontSize: 14,
  },
  countryOptionActive: {
    borderColor: PRIMARY_COLOR,
    backgroundColor: "#EEF6FF",
  },
  countryOptionCode: {
    color: "#6B7280",
    fontWeight: 700,
    fontSize: 12,
  },
};
